import React, {
    useRef,
    useMemo,
} from 'react';
import {
    useHistory,
    useLocation,
    useParams,
} from 'react-router-dom';
import 'url-search-params-polyfill';
import _noop from 'lodash/noop';

import useSearchQueryParam from 'bdvp-advanced-search/src/hooks/store/useSearchQueryParam';
import {
    useAdobeTarget,
    useClip,
    useIsEU,
    useModalStore,
    useTopic,
    useSearchQueryPlayback,
    usePlaybackSuggestions,
} from 'bdvp-common/src/hooks/store';
import {
    useDefaultTopic,
    useMediaConfigPlayback,
} from 'bdvp-common/src/hooks/config';
import { QUERY_PARAMS } from 'bdvp-common/src/utils/constants';
import { constants as modalConstants } from 'bdvp-graphql/src/resolvers/modals';

import Component from '../components/PlaybackPage';

const {
    SHARE_MODAL_NAME,
    FILTER_MODAL_NAME,
} = modalConstants;

const LIMIT = 36;

const PlaybackPage = () => {

    const { isEU } = useIsEU();
    const { contentId, userId } = useParams();
    const location = useLocation();
    const history = useHistory();
    const defaultTopic = useDefaultTopic();
    const { modals, isModalOpen } = useModalStore();
    const {
        searchQuery,
        queryType,
    } = useSearchQueryParam();

    const urlParams = new URLSearchParams(location.search);
    const contentSlug = location.search ? contentId.split(location.search)[0] : contentId;
    const feedType = urlParams.get(QUERY_PARAMS.feedType);
    const contentPreference = urlParams.get(QUERY_PARAMS.contentPreference);
    const searchPage = parseInt(urlParams.get(QUERY_PARAMS.searchPage)) || 0;
    const playlistTitle = urlParams.get(QUERY_PARAMS.playlistTitle);
    const suggestionsModel = urlParams.get(QUERY_PARAMS.suggestionsModel);
    let topicId = urlParams.get(QUERY_PARAMS.topicId);

    const clipSlug = userId ? `${userId}/${contentSlug}` : contentSlug;
    const queryId = useRef(clipSlug);
    const idType = useRef(userId ? 'USER_SLUG' : 'SLUG');
    const uid = useRef(userId);
    const shouldFetchTopic = topicId ? true : !!!searchQuery;

    if (!topicId) {

        topicId = defaultTopic;

    }

    const {
        loading: clipLoading,
        data: clipData,
        error: clipError,
    } = useClip(queryId.current, {
        idType: idType.current,
        userId: uid.current,
    });

    const mediaPlayback = clipData?.mediaPlayback?.[0] || {};
    const videoSlug = mediaPlayback.slug;
    const videoTranslationId = mediaPlayback.translationId;
    const config = useMediaConfigPlayback();
    const { adobeTarget: adobeTargetConfig = {} } = config;
    const { targetOffer } = useAdobeTarget(adobeTargetConfig);
    const isTargetOfferPending = !targetOffer;
    const mbox = useMemo(() => targetOffer?.[0]?.content?.[0] ?? {}, [targetOffer]);
    const adobeExperience = mbox.videoPlaylistExperience;
    const searchOptions = {
        limit: LIMIT,
        contentPreference,
        searchPage,
        queryType,
    };
    const topicOptions = {
        skip: isTargetOfferPending,
        adobeExperience,
        videoSlug,
        limit: LIMIT,
        videoTranslationId,
    };
    if (!isTargetOfferPending) {

        topicOptions.sessionId = document.cookie.match(/mbox=([^;]+)/)?.[1].match(/session#(.*)#/)?.[1];

    }
    const suggestionsOptions = {
        formatData: true,
    };

    let pageTopicId = topicId;
    let query = topicId;
    let options = topicOptions;
    let fetchHook = useTopic;

    if (!!searchQuery) {

        pageTopicId = 'search-results';
        query = searchQuery;
        options = searchOptions;
        fetchHook = useSearchQueryPlayback;
    
    }

    if (!!suggestionsModel) {

        pageTopicId = 'user-suggestions';
        query = suggestionsModel;
        options = suggestionsOptions;
        fetchHook = usePlaybackSuggestions;
    
    }

    const {
        loading: topicLoading,
        loadingMore: topicLoadingMore = false,
        data: topicData,
        error: topicError,
        fetchMoreData: topicFetchMore = _noop,
    } = fetchHook(query, options);

    const isShareModalOpen = isModalOpen(SHARE_MODAL_NAME);
    const isPlaybackReady = typeof mbox !== 'undefined' && typeof isEU !== 'undefined';

    return (
        <Component
            isEU={isEU}
            isPlaybackReady={isPlaybackReady}
            mbox={mbox}
            clipData={clipData}
            clipError={clipError}
            clipLoading={clipLoading}
            feedType={feedType}
            topicId={pageTopicId}
            topicLoading={isTargetOfferPending || topicLoading}
            topicLoadingMore={topicLoadingMore}
            topicData={topicData}
            topicError={topicError}
            topicFetchMore={topicFetchMore}
            location={location}
            history={history}
            clipSlug={clipSlug}
            isShareModalOpen={isShareModalOpen}
            isSearch={!shouldFetchTopic}
            isFilterActive={modals[FILTER_MODAL_NAME] || false}
            playlistTitle={playlistTitle}
            queryType={queryType}
        />
    );

};

export default PlaybackPage;
